import * as angular from "angular";
import * as _ from "lodash";
import { Campaign } from "./campaign.model";
import { Effort } from "./effort.model";
import { Field } from "./field.model";
import { User } from "./user.model";

export class Filter {
    public efforts: Effort[] | undefined;
    public custom_fields: Field[] | undefined;
    public division: any;
    public elections: any;
    public surveys: any[] | undefined;
    public age: any;

    public group?: any;
    public imports?: any;
    public zipcode?: any;
    public precinct?: any;
    public party?: any;
    public contact_method?: any;
    public employment_status?: any;
    public search?: any;
    public fid?: any;
    public exclude_unsubs?: any;
    public assigned?: any;
    public csqid?: any;
    public status?: any;
    public appType?: string;
    public campaign?: Campaign;
    public permission?: any;
    public direction?: string;
    public order?: any;
    public checked?: any;
    public selectedUsers?: any;

    public assigned_users?: any;
    public assigned_teams?: any;
    public list_status?: any;

    // Admin panel toolbar toggles. Plain booleans, so nonEmptyValues() drops
    // them — the admin endpoints read them off the filter object directly, or
    // via their own query param (see AdminCampaignService.all).
    public thisRegion?: "eu" | "usa" | boolean | null; // EU only / US only
    public active?: boolean;
    public attached?: boolean;
    public onlineOnly?: boolean;
    public device?: any;
    // Accounts list sort (AV-17963); the export reads it so the file follows
    // the on-screen order. Strings, so nonEmptyValues() drops them too.
    public sort?: string | null;
    public sortDir?: "asc" | "desc";
    // AV-17958 Activity lens on Accounts / Organisations. Plain values, so
    // nonEmptyValues() drops them; the endpoints read them as query params.
    public lens?: "directory" | "activity";
    public range?: number; // 1 | 7 | 30 | 90 days
    public trend?: string; // dropped | quiet | steady | growing | new | nodata

    // AV-17963 quick filter row. Strings/booleans, also dropped by
    // nonEmptyValues(); the admin endpoints read them straight off `filter`.
    public accountStatus?: string; // "Confirmed" | "Pending"
    public platform?: string; // "ios" | "android" | "web"
    public ostatus?: string; // "Active" | "Paused"
    public result?: string; // "success" | "failed"
    public showPresence?: boolean;

    public email?: any;
    public homephone?: any;
    public mobilephone?: any;

    constructor(data?: object) {
        if (data) {
            this.setData(data);
        }
    }

    setData(data: object): void {
        _.merge(this, data);

        angular.forEach(this.efforts, (effort: any, key: string) => {
            if (key === "checked" || !effort || !effort.last_interacted) {
                return;
            }
            const li = effort.last_interacted;
            if (typeof li.dateStart === "string") {
                li.dateStart = new Date(li.dateStart);
            }
            if (typeof li.dateEnd === "string") {
                li.dateEnd = new Date(li.dateEnd);
            }
        });
    }

    removeSelectedUsers(): void {
        angular.forEach(
            this.efforts,
            (effort: Effort | boolean, key: string) => {
                if (typeof effort == "boolean" || key === "checked") return;

                effort.selectedUsers = [];
            }
        );
    }

    nonEmptyValues(): object {
        let nonEmptyFilters = {};
        for (let key in this) {
            const value = (this as any)[key];
            if (
                (value && value.checked) ||
                (value && angular.isUndefined(value.checked))
            ) {
                if (key == "custom_fields") {
                    let activeCustomFields = this.activeFilteredCustomFields(
                        this.custom_fields
                    );
                    if (activeCustomFields.length > 0)
                        (nonEmptyFilters as any)[key] = activeCustomFields;
                } else if (key == "division") {
                    let divValues = this.division.values;
                    angular.forEach(divValues, (division?: any) => {
                        if (division) {
                            (nonEmptyFilters as any).division = divValues;
                        }
                    });
                } else if (
                    key == "search" &&
                    this.search.values &&
                    this.search.values != ""
                ) {
                    (nonEmptyFilters as any)[key] = this.search.values;
                } else if (
                    key == "email" &&
                    this.email.values &&
                    this.email.values != ""
                ) {
                    (nonEmptyFilters as any)[key] = this.email.values.replace(
                        /[&\/\\#,()~%'":*?<>{}]/g,
                        ""
                    );
                } else if (key == "efforts") {
                    let efforts = {};
                    angular.forEach(
                        this.efforts,
                        (fullEffort: Effort, effortID: string | number) => {
                            if (fullEffort && (fullEffort as any).checked) {
                                angular.forEach(
                                    fullEffort,
                                    (effortFilter: any, key: string) => {
                                        if (
                                            this.isEffortFilterActive(
                                                key,
                                                effortFilter
                                            )
                                        ) {
                                            if (!efforts[effortID]) {
                                                efforts[effortID] = {};
                                            }
                                            if (
                                                fullEffort.selectedUsers &&
                                                fullEffort.selectedUsers.length
                                            ) {
                                                efforts[
                                                    effortID
                                                ].selectedUsers = this.getReducedUserDetails(
                                                    fullEffort.selectedUsers
                                                );
                                            }
                                            efforts[effortID][key] =
                                                effortFilter;
                                        }
                                    }
                                );
                            }
                        }
                    );
                    if (!angular.equals({}, efforts)) {
                        (nonEmptyFilters as any).efforts = efforts;
                    }
                } else if (key == "elections") {
                    let elections = [];
                    for (let i = 0; i < this.elections.length; i++) {
                        if (
                            this.elections[i] &&
                            this.elections[i].checked &&
                            this.elections[i].values.length > 0
                        ) {
                            elections[i] = this.elections[i];
                        }
                    }
                    if (elections.length > 0) {
                        (nonEmptyFilters as any).elections = elections;
                    }
                } else if (key == "surveys") {
                    let surveys = this.nonEmptySurveys(this.surveys);
                    if (surveys.length > 0) {
                        (nonEmptyFilters as any).surveys = surveys;
                    }
                } else if (key == "zipcode") {
                    if (this.zipcode.values.length > 0) {
                        (nonEmptyFilters as any).zipcode = this.zipcode.values;
                    } else if (
                        this.zipcode.search &&
                        this.zipcode.search != ""
                    ) {
                        (nonEmptyFilters as any).zipcode = this.zipcode.search;
                    }
                } else if (
                    [
                        "income",
                        "square_footage",
                        "year_built",
                        "purchase_price",
                        "purchase_year",
                        "net_worth",
                    ].includes(key) &&
                    (value.values.min || value.values.max)
                ) {
                    (nonEmptyFilters as any)[key] = value.values;
                } else if (key == "street") {
                    (nonEmptyFilters as any)[key] = value;
                } else if (
                    key === "assigned_teams" &&
                    angular.isDefined(value.values) &&
                    value.values &&
                    value.values.length
                ) {
                    (nonEmptyFilters as any)[key] = this.getReducedTeamDetails(
                        value.values
                    );
                } else if (
                    key === "assigned_users" &&
                    angular.isDefined(value.values) &&
                    value.values &&
                    value.values.length
                ) {
                    (nonEmptyFilters as any)[key] = this.getReducedUserDetails(
                        value.values
                    );
                } else if (
                    this.hasOwnProperty(key) &&
                    angular.isDefined(value.values) &&
                    value.values &&
                    value.values.length
                ) {
                    (nonEmptyFilters as any)[key] = value.values;
                } else if (key === "age") {
                    if (this.age.values.length > 0) {
                        let activeAgeFields = [
                            {
                                exact_num: this.age.values[0].exact_num,
                                high_num: this.age.values[0].high_num,
                                low_num: this.age.values[0].low_num,
                            },
                        ];
                        (nonEmptyFilters as any)[key] = activeAgeFields;
                    }
                } else if (key === "search" && (this[key] as any).length > 0) {
                    (nonEmptyFilters as any)[key] = this[key];
                }
            }
        }
        return nonEmptyFilters;
    }

    countNonEmptyFilters(): number {
        return Object.keys(this.nonEmptyValues()).length;
    }

    private isEffortFilterActive(key: string, effortFilter: any): boolean {
        if (!effortFilter || !effortFilter.checked) {
            return false;
        }
        if (key === "last_interacted") {
            return !!(
                effortFilter.dateStart ||
                effortFilter.dateEnd ||
                effortFilter.selectedTemplateName
            );
        }
        return (
            angular.isDefined(effortFilter.values) &&
            effortFilter.values &&
            effortFilter.values.length > 0
        );
    }

    //strips out every non essential property to avoid very long URL
    getReducedUserDetails(users: User[]): object[] {
        let reducedUsers = [];
        angular.forEach(users, (user: User) => {
            reducedUsers.push({
                uid: user.uid,
                ufname: user.ufname,
                ulname: user.ulname,
            });
        });
        return reducedUsers;
    }

    getReducedTeamDetails(teams: any[]): object[] {
        return teams.map((t) => ({
            tid: t.tid,
            tname: t.tname,
        }));
    }

    //return only the custom fields with active filters.
    activeFilteredCustomFields(customFields: Field[]): Field[] {
        let filteredCustomFields = [];
        angular.forEach(customFields, (value: Field) => {
            if (value.checked) {
                let cfdefault = [];
                if (
                    value.cftid == "1" &&
                    ((value.hasOwnProperty("field_val") &&
                        value.field_val &&
                        value.field_val !== "") ||
                        (value.hasOwnProperty("any_value") &&
                            value.any_value == true) ||
                        (value.hasOwnProperty("no_value") &&
                            value.no_value == true))
                ) {
                    value.checked = true;
                    if (value.cfdefault != null) {
                        if (value.cfdefault.constructor !== Array) {
                            cfdefault.push(value.cfdefault);
                            value.cfdefault = cfdefault;
                        }
                    } else {
                        value.cfdefault = cfdefault;
                    }

                    filteredCustomFields.push(
                        this.filterCustomFieldProperties(value)
                    );
                } else if (
                    value.cftid == "2" &&
                    ((value.hasOwnProperty("exact_num") &&
                        value.field_val !== null) ||
                        (value.hasOwnProperty("low_num") &&
                            value.low_num !== null) ||
                        (value.hasOwnProperty("high_num") &&
                            value.high_num !== null) ||
                        (value.hasOwnProperty("any_value") &&
                            value.any_value == true) ||
                        (value.hasOwnProperty("no_value") &&
                            value.no_value == true))
                ) {
                    value.checked = true;
                    if (value.cfdefault != null) {
                        if (value.cfdefault.constructor !== Array) {
                            cfdefault.push(value.cfdefault);
                            value.cfdefault = cfdefault;
                        }
                    } else {
                        value.cfdefault = cfdefault;
                    }
                    filteredCustomFields.push(
                        this.filterCustomFieldProperties(value)
                    );
                } else if (
                    value.hasOwnProperty("field_val") &&
                    value.field_val.length > 0
                ) {
                    value.checked = true;
                    if (value.cftid == "3" || value.cftid == "6") {
                        if (value.cfdefault != null) {
                            if (value.cfdefault.constructor !== Array) {
                                cfdefault.push(value.cfdefault);
                                value.cfdefault = cfdefault;
                            }
                        } else {
                            value.cfdefault = cfdefault;
                        }
                    } else {
                        if (value.options != null) {
                            for (let i = 0; i < value.options.length; i++) {
                                if (value.options[i].cfodefault == 1) {
                                    cfdefault.push(value.options[i].cfoid);
                                }
                            }
                        }
                        if (value.options && value.options.length)
                            value.cfdefault = cfdefault;
                    }

                    filteredCustomFields.push(
                        this.filterCustomFieldProperties(value)
                    );
                }
            }
        });
        return filteredCustomFields;
    }

    //not all custom field properties need to be saved to location bar.
    filterCustomFieldProperties(field: Field): object {
        let filteredField = {};
        const filterProperties = [
            "cfid",
            "cftid",
            "field_val",
            "exact_num",
            "high_num",
            "no_value",
            "any_value",
            "match_type",
            "low_num",
            "cfdefault",
            "occupant_match",
        ];

        for (let i = 0; i < filterProperties.length; i++) {
            const property = filterProperties[i];
            if (field.hasOwnProperty(property))
                filteredField[property] = field[property];
        }

        return filteredField;
    }

    //not all custom field properties need to be saved to location bar.
    nonEmptySurveys(surveys: any[]): any[] {
        let non_empty_surveys = [];
        for (let i = 0; i < surveys.length; i++) {
            if (surveys[i].checked) {
                let non_empty_survey = null;
                if (surveys[i].signature && surveys[i].signature.length) {
                    non_empty_survey = _.cloneDeep(surveys[i]);
                }
                if (
                    angular.isDefined(surveys[i].questions) &&
                    surveys[i].questions.length > 0
                ) {
                    for (let j = 0; j < surveys[i].questions.length; j++) {
                        if (j === 0) {
                            if (!non_empty_survey) {
                                non_empty_survey = _.cloneDeep(surveys[i]);
                            }
                            non_empty_survey.questions = [];
                        }
                        if (
                            angular.isDefined(
                                surveys[i].questions[j].answers
                            ) &&
                            surveys[i].questions[j].answers.length > 0
                        ) {
                            non_empty_survey.questions.push(
                                surveys[i].questions[j]
                            );
                        }
                    }
                }
                if (
                    non_empty_survey &&
                    (non_empty_survey.questions.length ||
                        (non_empty_survey.signature &&
                            non_empty_survey.signature.length))
                ) {
                    non_empty_surveys.push(non_empty_survey);
                }
            }
        }
        return non_empty_surveys;
    }
}
